import React from 'react';

/**
 * Reusable skeleton loader components for loading states
 */

// Base skeleton component with animation
export const Skeleton = ({ className = '', width, height }) => {
  const style = {};
  if (width) style.width = width;
  if (height) style.height = height;

  return (
    <div
      className={`animate-pulse bg-gray-200 dark:bg-neutral-800 rounded ${className}`}
      style={style}
    />
  );
};

// Project card skeleton
export const ProjectCardSkeleton = () => {
  return (
    <div className="p-4 hover:bg-gray-50 dark:hover:bg-neutral-900 transition-colors border-b border-gray-200 dark:border-neutral-800 last:border-b-0">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          {/* Title */}
          <Skeleton className="h-5 w-3/4 mb-2" />
          
          {/* Metadata row */}
          <div className="flex items-center space-x-4 text-sm">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-16" />
          </div>
        </div>
        
        {/* Status badge */}
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
    </div>
  );
};

// Project list skeleton (multiple cards)
export const ProjectListSkeleton = ({ count = 5 }) => {
  return (
    <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 dark:bg-neutral-900 px-4 py-3 border-b border-gray-200 dark:border-neutral-800">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-8 w-24 rounded-lg" />
        </div>
      </div>
      
      {/* Project cards */}
      <div className="divide-y divide-gray-200 dark:divide-neutral-800">
        {Array.from({ length: count }).map((_, index) => (
          <ProjectCardSkeleton key={index} />
        ))}
      </div>
    </div>
  );
};

// Dashboard stats card skeleton
export const StatsCardSkeleton = () => {
  return (
    <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-8 w-8 rounded-lg" />
      </div>
      <Skeleton className="h-8 w-20 mb-2" />
      <Skeleton className="h-4 w-24" />
    </div>
  );
};

// Service interface skeleton
export const ServiceInterfaceSkeleton = () => {
  return (
    <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-6">
      {/* Header */}
      <div className="mb-6">
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-64" />
      </div>
      
      {/* Form fields */}
      <div className="space-y-4">
        <div>
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
        <div>
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-32 w-full rounded-lg" />
        </div>
        <div className="flex space-x-3">
          <Skeleton className="h-10 w-32 rounded-lg" />
          <Skeleton className="h-10 w-32 rounded-lg" />
        </div>
      </div>
    </div>
  );
};

// Table skeleton
export const TableSkeleton = ({ rows = 5, columns = 4 }) => {
  return (
    <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg overflow-hidden">
      {/* Table header */}
      <div className="bg-gray-50 dark:bg-neutral-900 px-4 py-3 border-b border-gray-200 dark:border-neutral-800">
        <div className="flex space-x-4">
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={index} className="h-4 flex-1" />
          ))}
        </div>
      </div>
      
      {/* Table rows */}
      <div className="divide-y divide-gray-200 dark:divide-neutral-800">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="px-4 py-3">
            <div className="flex space-x-4">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <Skeleton key={colIndex} className="h-4 flex-1" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Text skeleton (for paragraphs)
export const TextSkeleton = ({ lines = 3 }) => {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          className={`h-4 ${index === lines - 1 ? 'w-3/4' : 'w-full'}`}
        />
      ))}
    </div>
  );
};

// Card skeleton (generic)
export const CardSkeleton = ({ className = '' }) => {
  return (
    <div className={`bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-6 ${className}`}>
      <Skeleton className="h-6 w-1/3 mb-4" />
      <TextSkeleton lines={3} />
    </div>
  );
};

export default Skeleton;

